import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import api from '../lib/api'

type Marker = {
  vehicleArea: string
  damageType: string
  severity: string
  note?: string
  xPercent?: number
  yPercent?: number
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-base font-semibold text-slate-900 mb-3">{title}</h2>
      {children}
    </section>
  )
}

export default function PublicInspectionPage() {
  const { publicToken = '' } = useParams<{ publicToken: string }>()

  const [inspectorName, setInspectorName] = useState('')
  const [inspectorRole, setInspectorRole] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [locationName, setLocationName] = useState('')
  const [locationAddress, setLocationAddress] = useState('')
  const [odometer, setOdometer] = useState('')
  const [fuelLevel, setFuelLevel] = useState('')
  const [keysPresent, setKeysPresent] = useState<boolean | null>(null)
  const [operable, setOperable] = useState<boolean | null>(null)
  const [notes, setNotes] = useState('')
  const [noNewDamage, setNoNewDamage] = useState(false)
  const [signatureData, setSignatureData] = useState('')
  const [photoCategory, setPhotoCategory] = useState('FRONT')
  const [panelOrArea, setPanelOrArea] = useState('')
  const [markerDraft, setMarkerDraft] = useState<Marker>({ vehicleArea: '', damageType: '', severity: '' })
  const [markers, setMarkers] = useState<Marker[]>([])
  const [exceptionDraft, setExceptionDraft] = useState({ category: '', severity: '', description: '' })
  const [exceptions, setExceptions] = useState<Array<{ category: string; severity: string; description: string }>>([])
  const [issueDraft, setIssueDraft] = useState({ reportedArea: '', description: '', severity: 'UNKNOWN' as 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN' })
  const [reportedIssues, setReportedIssues] = useState<Array<{ id: string; reportedArea: string; description: string; severity: string }>>([])
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const session = useQuery({
    queryKey: ['inspection', publicToken, 'session'],
    queryFn: () => api.vehicleInspections.publicSession(publicToken).then((r) => r.data),
    enabled: Boolean(publicToken),
  })

  const beginMutation = useMutation({
    mutationFn: () =>
      api.vehicleInspections.publicBegin(publicToken, {
        inspectorName,
        inspectorRole,
        pickupOrDeliveryContactName: contactName,
        pickupOrDeliveryContactPhone: contactPhone,
        locationName,
        locationAddress,
      }),
    onSuccess: () => setMessage('Inspection started.'),
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to begin inspection'),
  })

  const markersMutation = useMutation({
    mutationFn: () => api.vehicleInspections.publicMarkers(publicToken, markers),
    onSuccess: () => setMessage('Condition markers saved.'),
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to save markers'),
  })

  const addExceptionMutation = useMutation({
    mutationFn: (payload: { category: string; severity: string; description: string }) =>
      api.vehicleInspections.publicAddException(publicToken, payload),
    onSuccess: () => setMessage('Exception added.'),
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to add exception'),
  })

  const submitMutation = useMutation({
    mutationFn: () =>
      api.vehicleInspections.publicSubmit(publicToken, {
        odometer: odometer ? Number(odometer) : null,
        fuelLevel,
        keysPresent,
        operable,
        notes,
        noNewDamage,
      }),
    onSuccess: () => setMessage('Inspection submitted.'),
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to submit inspection'),
  })

  const reportIssueMutation = useMutation({
    mutationFn: (payload: { reportedArea: string; description: string; severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN' }) =>
      api.vehicleInspections.publicReportIssue(publicToken, payload),
    onSuccess: (res) => {
      const issue = res.data.issue
      setReportedIssues((items) => [
        ...items,
        {
          id: issue.id,
          reportedArea: issue.reportedArea,
          description: issue.description,
          severity: issue.severity,
        },
      ])
      setMessage('Condition issue reported for triage.')
    },
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to report condition issue'),
  })

  const signatureMutation = useMutation({
    mutationFn: () =>
      api.vehicleInspections.publicSignature(publicToken, {
        signerName: inspectorName,
        signerRole: inspectorRole,
        signerType: 'DRIVER',
        signatureData,
      }),
    onSuccess: () => setMessage('Signature captured.'),
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to capture signature'),
  })

  const lockMutation = useMutation({
    mutationFn: () => api.vehicleInspections.publicLock(publicToken),
    onSuccess: () => setMessage('Inspection locked. Receipt complete.'),
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to lock inspection'),
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      api.vehicleInspections.publicUploadPhoto(publicToken, file, {
        photoCategory,
        panelOrArea: panelOrArea || undefined,
      }),
    onSuccess: () => setMessage('Photo uploaded.'),
    onError: (e: any) => setError(e?.response?.data?.error || 'Photo upload failed'),
  })

  const stage = session.data?.inspection.inspectionType || 'PICKUP'
  const title = useMemo(() => `${stage} Vehicle Inspection`, [stage])

  if (session.isLoading) {
    return <div className="min-h-screen bg-slate-100 p-6 text-sm text-slate-600">Loading inspection...</div>
  }

  if (session.isError || !session.data) {
    return <div className="min-h-screen bg-slate-100 p-6 text-sm text-red-700">{(session.error as any)?.response?.data?.error || 'Inspection link unavailable.'}</div>
  }

  const info = session.data.inspection

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">Diamond Dispatch</p>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-700 mt-1">
            Vehicle: {info.unit.year || ''} {info.unit.make || ''} {info.unit.model || ''} ({info.unit.vinMasked || 'VIN masked'})
          </p>
          <p className="text-sm text-slate-600">Order: {info.orderSummary.orderNumber || 'N/A'}</p>
          <p className="text-xs text-slate-500 mt-2">This one-time link is scoped to one unit and this inspection stage only.</p>
        </header>

        {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        {message && <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{message}</div>}

        <Section title="1) Inspector Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="rounded border px-3 py-2 text-sm" placeholder="Inspector name" value={inspectorName} onChange={(e) => setInspectorName(e.target.value)} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Inspector role" value={inspectorRole} onChange={(e) => setInspectorRole(e.target.value)} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Pickup/Delivery contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Pickup/Delivery contact phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Location name" value={locationName} onChange={(e) => setLocationName(e.target.value)} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Location address" value={locationAddress} onChange={(e) => setLocationAddress(e.target.value)} />
          </div>
          <button onClick={() => beginMutation.mutate()} className="mt-3 rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Begin Inspection</button>
        </Section>

        <Section title="2) Camera-First Photos">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select className="rounded border px-3 py-2 text-sm" value={photoCategory} onChange={(e) => setPhotoCategory(e.target.value)}>
              {[
                'FRONT', 'REAR', 'DRIVER_SIDE', 'PASSENGER_SIDE', 'FRONT_DRIVER_CORNER', 'FRONT_PASSENGER_CORNER',
                'REAR_DRIVER_CORNER', 'REAR_PASSENGER_CORNER', 'WINDSHIELD', 'VIN', 'ODOMETER', 'INTERIOR', 'KEYS', 'DAMAGE_CLOSEUP', 'OTHER',
              ].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input className="rounded border px-3 py-2 text-sm" placeholder="Panel/area (optional)" value={panelOrArea} onChange={(e) => setPanelOrArea(e.target.value)} />
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="rounded border px-3 py-2 text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) uploadMutation.mutate(file)
              }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">Required v1: at least 4 exterior photos; VIN photo for pickup when VIN is not confirmed; damage closeup for each marker/exception.</p>
        </Section>

        <Section title="3) Condition Markers">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input className="rounded border px-3 py-2 text-sm" placeholder="Vehicle area" value={markerDraft.vehicleArea} onChange={(e) => setMarkerDraft((m) => ({ ...m, vehicleArea: e.target.value }))} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Damage type" value={markerDraft.damageType} onChange={(e) => setMarkerDraft((m) => ({ ...m, damageType: e.target.value }))} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Severity" value={markerDraft.severity} onChange={(e) => setMarkerDraft((m) => ({ ...m, severity: e.target.value }))} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Note" value={markerDraft.note || ''} onChange={(e) => setMarkerDraft((m) => ({ ...m, note: e.target.value }))} />
          </div>
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => {
                if (!markerDraft.vehicleArea || !markerDraft.damageType || !markerDraft.severity) return
                setMarkers((items) => [...items, markerDraft])
                setMarkerDraft({ vehicleArea: '', damageType: '', severity: '' })
              }}
              className="rounded border border-slate-300 px-3 py-1 text-sm"
            >
              Add Marker
            </button>
            <button onClick={() => markersMutation.mutate()} className="rounded bg-slate-900 px-3 py-1 text-sm text-white">Save Markers</button>
          </div>
          {!!markers.length && (
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {markers.map((m, idx) => (
                <li key={`${m.vehicleArea}-${idx}`} className="rounded border px-2 py-1">{m.vehicleArea} • {m.damageType} • {m.severity}</li>
              ))}
            </ul>
          )}
        </Section>

        {stage === 'DELIVERY' && (
          <Section title="4) Delivery Exceptions">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input className="rounded border px-3 py-2 text-sm" placeholder="Category" value={exceptionDraft.category} onChange={(e) => setExceptionDraft((d) => ({ ...d, category: e.target.value }))} />
              <input className="rounded border px-3 py-2 text-sm" placeholder="Severity" value={exceptionDraft.severity} onChange={(e) => setExceptionDraft((d) => ({ ...d, severity: e.target.value }))} />
              <input className="rounded border px-3 py-2 text-sm" placeholder="Description" value={exceptionDraft.description} onChange={(e) => setExceptionDraft((d) => ({ ...d, description: e.target.value }))} />
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => {
                  if (!exceptionDraft.category || !exceptionDraft.severity || !exceptionDraft.description) return
                  setExceptions((items) => [...items, exceptionDraft])
                  addExceptionMutation.mutate(exceptionDraft)
                  setExceptionDraft({ category: '', severity: '', description: '' })
                }}
                className="rounded border border-slate-300 px-3 py-1 text-sm"
              >
                Add Exception
              </button>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={noNewDamage} onChange={(e) => setNoNewDamage(e.target.checked)} />
                No new damage
              </label>
            </div>
            {!!exceptions.length && (
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                {exceptions.map((ex, idx) => (
                  <li key={`${ex.category}-${idx}`} className="rounded border px-2 py-1">{ex.category} • {ex.severity} • {ex.description}</li>
                ))}
              </ul>
            )}
          </Section>
        )}

        <Section title="4b) Condition Issue Report (Private)">
          <p className="text-xs text-slate-500">Use this to flag potential damage or condition concerns for private dispatcher/carrier triage.</p>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              className="rounded border px-3 py-2 text-sm"
              placeholder="Reported area"
              value={issueDraft.reportedArea}
              onChange={(e) => setIssueDraft((d) => ({ ...d, reportedArea: e.target.value }))}
            />
            <select
              className="rounded border px-3 py-2 text-sm"
              value={issueDraft.severity}
              onChange={(e) => setIssueDraft((d) => ({ ...d, severity: e.target.value as any }))}
            >
              <option value="UNKNOWN">UNKNOWN</option>
              <option value="LOW">LOW</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="HIGH">HIGH</option>
            </select>
            <input
              className="rounded border px-3 py-2 text-sm md:col-span-1"
              placeholder="Description"
              value={issueDraft.description}
              onChange={(e) => setIssueDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </div>
          <button
            onClick={() => {
              if (!issueDraft.reportedArea || !issueDraft.description) return
              reportIssueMutation.mutate(issueDraft)
              setIssueDraft({ reportedArea: '', description: '', severity: 'UNKNOWN' })
            }}
            className="mt-2 rounded border border-slate-300 px-3 py-1 text-sm"
          >
            Report Issue
          </button>
          {!!reportedIssues.length && (
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {reportedIssues.map((issue) => (
                <li key={issue.id} className="rounded border px-2 py-1">
                  {issue.reportedArea} • {issue.severity} • {issue.description}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="5) Notes / Condition">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="rounded border px-3 py-2 text-sm" placeholder="Odometer" value={odometer} onChange={(e) => setOdometer(e.target.value)} />
            <input className="rounded border px-3 py-2 text-sm" placeholder="Fuel level" value={fuelLevel} onChange={(e) => setFuelLevel(e.target.value)} />
            <label className="text-sm text-slate-700 inline-flex items-center gap-2">
              <input type="checkbox" checked={keysPresent === true} onChange={(e) => setKeysPresent(e.target.checked)} /> Keys present
            </label>
            <label className="text-sm text-slate-700 inline-flex items-center gap-2">
              <input type="checkbox" checked={operable === true} onChange={(e) => setOperable(e.target.checked)} /> Operable
            </label>
          </div>
          <textarea className="mt-3 w-full rounded border px-3 py-2 text-sm" rows={3} placeholder="Inspection notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <button onClick={() => submitMutation.mutate()} className="mt-3 rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Submit Inspection</button>
        </Section>

        <Section title="6) Signature & Lock">
          <textarea
            className="w-full rounded border px-3 py-2 text-sm"
            rows={3}
            placeholder="Paste signature data URL or simple text signature acknowledgement"
            value={signatureData}
            onChange={(e) => setSignatureData(e.target.value)}
          />
          <div className="mt-3 flex gap-2">
            <button onClick={() => signatureMutation.mutate()} className="rounded border border-slate-300 px-3 py-2 text-sm">Capture Signature</button>
            <button onClick={() => lockMutation.mutate()} className="rounded bg-emerald-700 px-3 py-2 text-sm text-white">Submit & Lock Evidence</button>
          </div>
        </Section>
      </div>
    </div>
  )
}
